import { Fragment, useEffect, useRef, useState } from 'react';

/**
 * Retrieves the translation of text.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/packages/packages-i18n/
 */
import { __ } from '@wordpress/i18n';

/**
 * React hook that is used to mark the block wrapper element.
 * It provides all the necessary props like the class name.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/packages/packages-block-editor/#useblockprops
 */
import { useBlockProps, InspectorControls } from '@wordpress/block-editor';

import {
	TextareaControl,
	SelectControl,
	PanelBody,
	TextControl,
	Button,
	__experimentalDivider as Divider
} from '@wordpress/components';

export const backgroundColorMap = [
	'#008293',
	'#cae9e7',	
	'#f390b4',
	'#e10387',
	'#ffe73c',
	'#f44f28',
	'#ff8c00'
]

export const getChartConfig = (attributes) => {
	return {
			type: attributes.chartType,
			data: {
				labels: attributes.labels,
				datasets: attributes.datasets?.map(({ name, data }, i) => ({
					label: name,
					data,
					backgroundColor: attributes.chartType === 'pie' ? data.map((data, i) => (backgroundColorMap[i % 7])) : attributes.datasets.length > 1 ?  backgroundColorMap[i % 7] : backgroundColorMap
				}))
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				scales: {
					x: {
						border: {
							display: attributes.chartType !== 'pie'
						},
						grid: {
							display: false,
							drawTicks: false
						},
						ticks: {
							display: attributes.chartType !== 'pie',
							color: '#333333',
							padding: 10,
							font: {
								size: 24,
								family: 'Raleway',
								weight: 700
							}
						},
						title: {
							display: !!attributes.xAxis,
							text: attributes.xAxis,
							color: '#333333',
							font: {
								size: 24,
								family: 'Raleway',
								weight: 700
							}
						}							
					},
					y: {
						border: {
							display: attributes.chartType !== 'pie'
						},
						grid: {
							display: false,
							drawTicks: false,
						},
						ticks: {
							display: false
						},
						title: {
							display: !!attributes.yAxis,
							text: attributes.yAxis,
							color: '#333333',
							padding: {
								bottom: 15
							},
							font: {
								size: 24,
								family: 'Raleway',
								weight: 700
							}
						}	
					}
				},
				plugins: {
					title: {
						display: !!attributes.chartTitle,
						text: attributes.chartTitle,
						color: '#333333',
						font: {
							size: 24,
							family: 'Raleway',
							weight: 700
						}
					},
					legend: {
						display: !attributes.isMultiChart,
						position: 'bottom',
						align: 'start',
						labels: {
							color: '#333333',
							font: {
								size: 16,
								family: 'Lato'
							}
						}
					}
				}
			}
		}
}

export const initChartFromAttributes = (element, attributes) => {
	return new Chart(element, getChartConfig(attributes));
}

/**
 * The edit function describes the structure of your block in the context of the
 * editor. This represents what the editor will render when the block is used.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-edit-save/#edit
 *
 * @return {Element} Element to render.
 */
export default function Edit({ attributes, setAttributes, isSelected }) {

	const [chart, setChart] = useState(null);

	const handleAddDataset = () => {
		const datasets = attributes.datasets ? [...attributes.datasets] : [];
		datasets.push({
			name: '',
			data: null
		});
		if (chart) {
			chart.data.datasets = datasets;
			chart.update();
		}
		setAttributes({ datasets })
	}

	const handleDeleteDataset = (dataset) => {
		const datasets = [...attributes.datasets];
		datasets.splice(dataset, 1);
		if (chart) {
			chart.data.datasets.splice(dataset, 1);
			chart.update();
		}
		setAttributes({ datasets });
	}
	
	const onChangeChartType = (newValue) => {
		if (chart) {
			chart.destroy();
			setChart(initChartFromAttributes(chartRef.current, {
				...attributes,
				chartType: newValue
			}));
		}
		setAttributes({ chartType: newValue })
	}

	const onChangeChartTitle = (newValue) => {
		if (chart) {
			chart.options.plugins.title.text = newValue;
			chart.update();
		}

		setAttributes({chartTitle: newValue});
	}
	const onChangeXAxis = (newValue) => {
		if (chart) {
			chart.options.scales.x.title = {
				display: true,
				text: newValue
			};
			chart.update();
		}
		setAttributes({ xAxis: newValue });
	}
	const onChangeYAxis = (newValue) => {
		if (chart) {
			chart.options.scales.y.title = {
				display: true,
				text: newValue
			};
			chart.update();
		}
		setAttributes({ yAxis: newValue });
	}
	const onChangeData = (newValue, dataset) => {
		const { datasets } = attributes;
		const newData = newValue.split(',');
		datasets[dataset].data = newData;
		if (chart) {
			chart.data.datasets[dataset].data = newData;
			chart.update();
		}
		setAttributes({ datasets })
	}
	const onChangeDatasetName = (newValue, dataset) => {
		const { datasets } = attributes;
		datasets[dataset].name = newValue;
		if (chart) {
			chart.data.datasets[dataset].label = newValue;
			chart.update();
		}
		setAttributes({ datasets })
	}
	const onChangeLabels = (newValue) => {
		const newLabels = newValue.split(',');
		if (chart) {
			chart.data.labels = newLabels;
			chart.update();
		}
		setAttributes({labels: newLabels});
	}
	
	const chartRef = useRef();

	useEffect(() => {
		if (chartRef.current && !chart) {
			setChart(initChartFromAttributes(chartRef.current, attributes));
		}
	}, [chartRef.current]);

	let datasetFields;

	if (attributes.datasets?.length) {
		datasetFields = attributes.datasets.map(({ name, data }, i) => (
			<Fragment key={i}>
				<TextControl
						__nextHasNoMarginBottom
						label="Name"
						defaultValue={name}
						onChange={(value) => onChangeDatasetName(value, i)}
				/>
				<TextareaControl 
						__nextHasNoMarginBottom
						label="Data"
						defaultValue={data ? data.join(',') : null}
						onChange={(value) => onChangeData(value, i)}				
				/>
				<Button
					icon="no-alt"
					isDestructive={true}
					onClick={() => handleDeleteDataset(i)}
				 >Delete dataset</Button>
				 { i < attributes.datasets.length - 1 ? (
					<Divider />
				 ) : null}
			</Fragment>
		));
	}

	return (
		<div { ...useBlockProps() }>
			<div style={{ position: 'relative', width: '100%', height: 'auto', minHeight: '500px'}}>
				<canvas ref={chartRef}></canvas>
			</div>
			{isSelected && (
				<InspectorControls key="setting">
					<PanelBody title="Settings">
							<SelectControl
								__nextHasNoMarginBottom
								label="Chart Type"
								value={ attributes.chartType }
								options={ [
									{ value: 'pie', label: 'Pie' },
									{ value: 'bar', label: 'Bar' },
									{ value: 'line', label: 'Line' },
								] }
								onChange={ onChangeChartType }
							/>
							<TextControl
								__nextHasNoMarginBottom
								label="Chart Title"
								defaultValue={ attributes.chartTitle }
								onChange={onChangeChartTitle}
							/>
							<TextControl
								__nextHasNoMarginBottom
								label="X Axis Title"
								defaultValue={ attributes.xAxis }
								onChange={onChangeXAxis}
							/>
							<TextControl
								__nextHasNoMarginBottom
								label="Y Axis Title"
								defaultValue={ attributes.yAxis }
								onChange={onChangeYAxis}
							/>
							<TextareaControl
								__nextHasNoMarginBottom
								label="Labels"
								defaultValue={attributes.labels}
								onChange={onChangeLabels}
							/>							
					</PanelBody>
					<PanelBody
						title='Datasets'
					>
						{datasetFields}
						<Divider />
						<Button variant='primary' onClick={handleAddDataset}>Add dataset</Button>
					</PanelBody>
			</InspectorControls>
			)}
		</div>
	);
}
